<!-- eslint-disable vue/multi-word-component-names -->
<template>
    <USelect
        ref="selectRef"
        v-model="model"
        :items="items"
        value-key="value"
        label-key="label"
        :placeholder="placeholder"
        :ui="ui"
        :content="contentProps"
        @change="onChange"
    />
</template>

<script setup>
import { computed, ref } from 'vue'

// Component name: SelectInput (multi-word compliance)
// Define emits
const emit = defineEmits(['change'])

// Define props
const props = defineProps({
    options: {
        required: true,
        type: Array,
    },
    optionLabel: {
        default: null,
        required: false,
        type: String,
    },
    optionValue: {
        default: null,
        required: false,
        type: String,
    },
    placeholder: {
        default: '',
        required: false,
        type: String,
    },
})

const model = defineModel()

// USelect's `value-key` always extracts a keyed field from each item, so
// options are normalized into { label, value } pairs here — `value` is
// either the extracted `optionValue` field or the whole raw option, matching
// the "bind whole object when optionValue is unset" behavior this component
// has always had.
const items = computed(() =>
    props.options.map((option) => ({
        label: props.optionLabel
            ? option?.[props.optionLabel]
            : typeof option === 'object' && option !== null
              ? ''
              : option,
        value: props.optionValue ? option?.[props.optionValue] : option,
    }))
)

const ui = {
    base: 'w-full cursor-pointer border border-taupe-300 rounded-md flex items-center px-3 py-2 text-sm text-taupe-800 bg-white focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500',
    trailing: 'pr-3',
    trailingIcon: 'text-taupe-500',
    content:
        'border border-taupe-300 rounded-md mt-0.5 bg-white ring-0 shadow-none',
    viewport: 'overflow-y-auto',
    item: 'cursor-pointer px-3 py-2 text-sm rounded-sm hover:bg-sky-200/50',
}

const selectRef = ref(null)

// Reka UI's Select (which USelect wraps) restores keyboard focus to its own
// trigger once the dropdown panel finishes unmounting after closing — but
// that unmount is deferred via an internal setTimeout, not synchronous with
// the close (see reka-ui's SelectContent.vue: `renderPresence` is flipped
// off in a `setTimeout`, "nextTick is not enough"). Under CPU contention
// that timer can fire well after focus has already moved on to a different
// field (e.g. the Attribute/Value inputs next to this selector in
// ContextRule.vue), silently reclaiming focus onto this trigger. Since the
// trigger treats Enter/Space/Arrow keys as "open the menu" (reka-ui
// SelectTrigger.vue OPEN_KEYS), a keystroke meant for whatever field the
// user has since moved to can instead reopen this already-closed dropdown —
// observed in CI as target-rule creation getting stuck with the Criteria
// selector spontaneously reopening after picking "Context Attribute",
// swallowing the Enter meant to commit the Attribute value and leaving the
// form unable to submit.
//
// Suppress reka's deferred auto-focus-restore and take deterministic
// control of focus ourselves at selection time instead.
const contentProps = {
    onCloseAutoFocus: (event) => event.preventDefault(),
}

function onChange(value) {
    emit('change', value)
    selectRef.value?.triggerRef?.blur?.()
}
</script>
